/**
 * Textos de ajuda do jogo (ícone "i" e passos do tutorial), resumidos do GDD (README.md).
 * Cada tópico é curto: título e um ou mais parágrafos.
 */
export interface HelpTopic {
  title: string;
  body: string[];
}

export const HELP = {
  // ─── Partida e lobby ───────────────────────────────────────────────────
  'match.players': {
    title: 'Governos',
    body: [
      'Cada partida tem de 4 a 12 governos, humanos ou bots.',
      'Cada governo recebe uma capital sorteada, um por continente; com mais de 6 governos, os continentes maiores recebem dois.',
    ],
  },
  'match.turns': {
    title: 'Turnos por dia',
    body: [
      'O turno vira à meia-noite (ou à meia-noite e ao meio-dia) no fuso da partida.',
      'Até a virada você define e muda as ordens livremente. Pesquisa, construção, produção, espionagem, diplomacia e comércio acontecem em tempo real, a qualquer hora.',
    ],
  },
  'match.timezone': {
    title: 'Fuso horário',
    body: ['Define o horário da virada dos turnos. Use um fuso IANA, como America/Sao_Paulo.'],
  },
  'match.victory': {
    title: 'Modo de vitória',
    body: [
      'Dominação total: vence quem destruir todos os outros governos.',
      'Conquista de capitais: controlar mais de 50% das capitais iniciais por 3 turnos seguidos.',
      'Supremacia, Maior potência econômica e Maior potência militar: após N turnos vence a maior Pontuação de Poder, o maior PIB ou o maior Poder militar.',
      'Terror nuclear: vence o primeiro a detonar com sucesso uma arma nuclear em território inimigo.',
      'Controle: mais de 70% dos territórios terrestres por 3 turnos seguidos.',
    ],
  },
  'match.turnLimit': {
    title: 'Turno limite (N)',
    body: ['Nos modos com prazo (Supremacia, potência econômica e militar), a partida termina neste turno.'],
  },
  'match.allies': {
    title: 'Limite de aliados',
    body: ['Quantos aliados cada governo pode ter ao mesmo tempo (padrão: 2).'],
  },
  'match.bots': {
    title: 'Bots',
    body: [
      'Bots são governos controlados pelo computador. Jogam com as mesmas regras e a mesma neblina de guerra dos humanos, recebem o nome do país de origem e aparecem marcados com [bot].',
      'No lobby, quem criou a partida escolhe iniciar só com os inscritos ou completar as vagas com bots.',
      'A dificuldade muda a qualidade das decisões, nunca dá recursos ou informação extra.',
    ],
  },
  'match.singlePlayer': {
    title: 'Single player',
    body: [
      'Você contra bots, com as mesmas regras. A diferença: não há relógio — o turno vira quando você clicar em Próximo turno, no cabeçalho.',
      'Ao clicar, os bots agem e dão as ordens do turno; depois a virada é resolvida e começa o próximo turno.',
      'Modo por turno: não há relógio. Construção, pesquisa, produção e missões de agentes concluem na hora (o Palácio continua contando turnos); recursos e impostos entram na virada.',
    ],
  },
  'match.private': {
    title: 'Convite e partida fechada',
    body: [
      'Toda partida tem um código de convite. Compartilhe o código ou o link do lobby para chamar outros jogadores.',
      'Partida fechada não aparece na lista de partidas: só entra quem tiver o código.',
    ],
  },
  'fleet.overview': {
    title: 'Frota',
    body: [
      'Cada navio, aeronave e helicóptero tem matrícula (CT-03) e pode receber um nome. A lista mostra onde está, a ocupação (aviação no porta-aviões, tropas nos navios de desembarque) e as missões que partem do local.',
      'Slots de carga: clique num slot vazio para embarcar do porto ou de um litoral próprio/aliado da zona (escolha a origem no seletor). SSBN leva 4 SLBM e até 2 ogivas nucleares; cruzador e submarino nuclear, 4 mísseis de cruzeiro. O míssil chega com ogiva convencional; no SSBN, o slot recebe ogiva nuclear. Lançar (abaixo do slot) abre a missão só para escolher o alvo — um lançamento por navio por turno.',
      'Navio de desembarque na zona: slot vazio embarca tropas e veículos de um litoral próprio/aliado; slot cheio desembarca. No porto, tropas embarcam com o navio na ordem de Marcha; invadir território inimigo também é ordem de Marcha.',
      'Marcha e missão por unidade: cada navio mostra para onde vai neste turno (fixado na Frota ou previsto pela ordem do mapa) e pode marchar sozinho para o destino escolhido; cada aeronave pode receber uma missão só dela, inclusive Realocação entre Bases aéreas e porta-aviões.',
      'As ordens do mapa movem quantidades: vão primeiro as unidades fixadas na Frota, depois os navios com mísseis a bordo, e as com nome ficam. Nas perdas, quem é atingido é sorteado primeiro entre as que saíram em marcha ou missão (a carga afunda com o navio). Unidades perdidas continuam na lista, riscadas; perder uma unidade com nome vira notícia.',
    ],
  },
  'market.overview': {
    title: 'Bolsa de recursos',
    body: [
      'Não há compra avulsa: a cada turno o mercado oferece uma lista fixa de lotes (tipo, quantidade e preço). Nem todo recurso aparece em todo turno. Lote comprado fica na lista como vendido; a lista renova na virada.',
      'O preço muda a cada turno: volta aos poucos à média (2× a referência), sobe com a procura e com a escassez mundial, cai com as vendas e com a fartura. Urânio enriquecido não é ofertado pelo mercado: só aparece se um governo vender.',
    ],
  },
  'market.auction': {
    title: 'Leilões da Bolsa',
    body: [
      'A cada turno, 5 lotes grandes dos recursos mais comprados no turno anterior vão a leilão. O primeiro lance é de pelo menos o valor de mercado do lote; cada lance seguinte, de pelo menos 10 $ acima do maior. Todos veem o maior lance e o governo que o deu.',
      'O lance sai do Tesouro na hora e volta inteiro se outro governo cobrir. Vence quem tiver o maior lance na virada: o lote chega no território escolhido (ou na capital, se ele foi perdido). O que passar da capacidade do armazém se perde.',
    ],
  },
  'market.sell': {
    title: 'Vender na Bolsa',
    body: [
      'A venda é livre e paga na hora: 1/4 do preço de compra do turno (metade da referência na média).',
      'A quantidade vendida entra na lista como revenda até a virada, com o nome do governo que vendeu; outro governo pode comprá-la.',
    ],
  },
  'match.luck': {
    title: 'Sorte no combate',
    body: [
      'Com a sorte ligada, a força de cada lado é multiplicada por um valor entre 0,9 e 1,1. Desligada, o combate é determinístico.',
    ],
  },
  'match.sharedVictory': {
    title: 'Vitória compartilhada',
    body: ['Nas condições de território e de capitais, os membros de uma aliança somam o que controlam.'],
  },
  'match.inactive': {
    title: 'Inatividade',
    body: [
      '3 turnos sem acessar: governo interino (todos os territórios em Defesa; pesquisa e construção param).',
      '10 turnos: colapso — os territórios viram neutros. Com esta opção, um bot assume o governo no lugar do colapso.',
    ],
  },
  'match.fast': {
    title: 'Partida rápida',
    body: [
      'Para testar: turnos de 5 minutos e tempo real 24× mais rápido (construções e pesquisas terminam em minutos).',
    ],
  },

  // ─── Cabeçalho da partida ──────────────────────────────────────────────
  'header.dtg': {
    title: 'DTG',
    body: ['Data e hora no formato militar (Zulu, UTC), pelo relógio do servidor.'],
  },
  'header.treasury': {
    title: 'Tesouro',
    body: [
      'Dinheiro nacional, sem limite. Vem dos impostos dos territórios consolidados (e do bônus de continente) e paga pesquisa, construção, produção e manutenção.',
      'Os impostos entram ao longo do turno no multiplayer, ou de uma vez na virada no single player ("+N $/turno" é o total do turno). A manutenção é cobrada na virada.',
      'Tesouro negativo: pesquisa e construção param e todas as unidades ficam com −10% de ATQ e DEF.',
    ],
  },
  'header.specials': {
    title: 'Ordens especiais (★)',
    body: [
      'Versões fortes das ordens: Marcha ★ +20% ATQ, Defesa ★ +50% DEF, Apoio ★ 1,5×, Saque ★ destrói indústria, Consolidar ★ +50 de Controle.',
      'Você tem tantas por turno quanto o nível do Estado-Maior na capital.',
    ],
  },
  'header.turn': {
    title: 'Virada do turno',
    body: [
      'Contagem regressiva até a virada. Na virada as ordens são travadas e resolvidas nesta ordem: saques, missões aéreas e de mísseis, mar, terra, logística, consolidação e revoltas, produção, notícias.',
    ],
  },

  // ─── Mapa e ordens ─────────────────────────────────────────────────────
  'map.overview': {
    title: 'Carta de situação',
    body: [
      'Azul é seu, verde é aliado, vermelho é inimigo em guerra, cinza pontilhado é neutro. Listrado indica território ocupado (ainda não consolidado).',
      'Clique num território ou zona marítima para ver o que se sabe dele; se houver unidades suas, o painel de ordem abre.',
    ],
  },
  'map.layers': {
    title: 'Camadas do mapa',
    body: [
      'Ligue e desligue unidades (símbolos militares APP-6), ordens, missões e logística, zonas marítimas, rótulos e grade de referência.',
    ],
  },
  'map.fog': {
    title: 'Neblina de guerra',
    body: [
      'Você só sabe o que espiona. De territórios vizinhos aos seus vê só uma faixa de tropas (pequena, média, grande…) e se há instalações militares.',
      'Relatórios de espionagem mostram o número exato, com a idade da informação ("visto no turno T").',
    ],
  },
  'map.objectives': {
    title: 'Objetivos de governo',
    body: [
      'Metas da primeira fase da partida: expandir para 3 e 8 territórios, construir a primeira base aérea ou naval, chegar à Era 2, vencer um combate contra outro governo e dominar um continente.',
      'Cada objetivo dá a recompensa uma única vez, na hora em que é concluído: dinheiro no Tesouro, recursos na capital.',
    ],
  },
  'map.log': {
    title: 'Registro de eventos',
    body: ['Combates, construções e pesquisas concluídas, relatórios e avisos chegam aqui em tempo real.'],
  },
  'orders.types': {
    title: 'Tipos de ordem',
    body: [
      'Cada local com unidades suas recebe uma ordem por turno. Sem ordem, defende sem bônus.',
      'Marcha: move e ataca. Defesa: +25% DEF. Apoio: soma força a um combate vizinho sem se mover (sofre metade das perdas). Saque: rouba recursos de um vizinho sem conquistar. Consolidar: +34 de Controle no ocupado (3 turnos para consolidar), ou +50% de impostos no consolidado.',
    ],
  },
  'orders.special': {
    title: 'Ordem especial (★)',
    body: [
      'Usa uma das ordens especiais do turno para fortalecer esta ordem. Limite = nível do Estado-Maior na capital.',
    ],
  },
  'orders.objective': {
    title: 'Objetivo',
    body: [
      'Destino da marcha ou alvo do apoio/saque. Ao escolher o tipo, os destinos ao alcance ficam destacados em âmbar no mapa — clique neles ou escolha na lista.',
      'Entrar em território neutro ou inimigo encerra o movimento e inicia um combate.',
    ],
  },
  'orders.forces': {
    title: 'Forças empenhadas',
    body: [
      'Quantas unidades vão. Todo território terrestre precisa manter pelo menos 1 soldado.',
      'Use "Dividir" para mandar partes da tropa a destinos diferentes.',
      'Com navios de desembarque, as tropas e veículos marcados embarcam junto; o painel mostra a carga e a capacidade.',
    ],
  },
  'orders.correlation': {
    title: 'Correlação de forças',
    body: [
      'Estimativa do engine com o que você sabe do alvo. Com informação incompleta, mostra uma faixa.',
      'O combate é um único cálculo: o lado mais forte vence e as perdas dependem da proporção (2 : 1 → vencedor perde ~26%, perdedor ~74%). Empate exato: vence o defensor.',
    ],
  },
  'orders.save': {
    title: 'Salvar a ordem',
    body: [
      'A ordem só vale depois de "Salvar ordem": ela fica gravada no servidor (você pode fechar o navegador ou trocar de dispositivo). Depois de salvar, cancelar ou remover, o território é desmarcado.',
      'Ordem inválida (sem destino ou com carga acima da capacidade) não pode ser salva; o motivo aparece no painel. "Remover ordem" apaga a ordem salva.',
    ],
  },
  'orders.revolt': {
    title: 'Risco de revolta',
    body: [
      'Território ocupado com guarnição abaixo da exigida pode se revoltar: chance = 50% × (1 − soldados/exigidos) × (1 − Controle/100).',
      'Na revolta surge uma milícia que ataca a guarnição; se vencer, o território volta a ser neutro.',
    ],
  },
  'orders.missions': {
    title: 'Missões',
    body: [
      'Aeronaves e mísseis não usam ordens de território: executam missões a partir de uma Base aérea, porta-aviões, silo ou lançador. Cada base faz tantas missões por turno quanto o seu nível.',
      'Bombardeio, apoio aéreo, patrulha, ataque naval, lançamento de míssil, transporte aéreo e realocação. No mapa da missão aparecem o alcance (destinos válidos) e a rota.',
    ],
  },
  'orders.detection': {
    title: 'Detecção e combate aéreo',
    body: [
      'Em cada território inimigo do caminho e no destino, a missão pode ser detectada: P = detectabilidade × CA ÷ (CA + 20). No caminho a chance cai pela metade; stealth só é detectado no destino.',
      'Detectada, enfrenta antiaéreos e caças. Caças de escolta só acompanham até o limite do seu alcance.',
    ],
  },
  'orders.history': {
    title: 'Histórico de combates',
    body: [
      'Todos os combates em que você atacou ou foi atacado, do mais recente ao mais antigo: origem → destino, tipo, forças, resultado e suas perdas.',
      'Use os filtros (Feitos, Recebidos) e "Relatório" para ver o combate completo do turno.',
    ],
  },
  'orders.combatReport': {
    title: 'Relatório de combates',
    body: ['Combates do turno resolvido em que você participou: forças, vencedor e perdas de cada lado.'],
  },

  // ─── Territórios, economia e pesquisa ──────────────────────────────────
  'territory.states': {
    title: 'Estados de um território',
    body: [
      'Neutro: defendido por milícia. Ocupado: seus soldados tomaram; produz 50% e não paga impostos. Consolidado: Controle 100; produção total, impostos e todas as construções.',
      'Desguarnecido (sem soldados) perde 25 de Controle por turno. Contestado: há unidades inimigas sem soldados; não produz.',
    ],
  },
  'territory.control': {
    title: 'Controle',
    body: [
      'De 0 a 100. Sobe com a ordem Consolidar (+34, ★ +50: 3 turnos, ou 2 com ★), se não houve combate no turno. Com guarnição abaixo da exigida, rende metade.',
      'Em 100 o território fica consolidado.',
    ],
  },
  'territory.garrison': {
    title: 'Guarnição exigida',
    body: [
      'Metade da guarnição neutra (mínimo 3 soldados). Abaixo dela, Consolidar rende metade e há risco de revolta.',
    ],
  },
  'territory.stock': {
    title: 'Estoque',
    body: [
      'Multiplayer: a produção é contínua — matéria-prima, indústrias e impostos rendem aos poucos, proporcionalmente ao tempo do turno (num turno de 24 h, ao longo do dia). Single player: tudo entra de uma vez na virada. O total por turno é o mesmo.',
      'Matéria-prima e insumos ficam no território onde foram produzidos (500 de cada, +1.000 por nível de Armazém). O excedente se perde.',
      'Para construir ou produzir, os recursos precisam estar no território — use rotas de logística.',
    ],
  },
  'territory.construction': {
    title: 'Construção',
    body: [
      'Instalações têm níveis de 1 a 5; cada nível custa 1,8× e leva 1,6× o anterior. Uma construção por vez por território (+1 com Engenharia civil).',
      'Ocupado só constrói Quartel, Fortificações, Bateria antiaérea e Radar (com Engenheiros, também Infraestrutura e Industrial).',
    ],
  },
  'territory.production': {
    title: 'Produção de unidades',
    body: [
      'Unidades são produzidas numa instalação militar (Quartel, Base aérea, Base naval…) e ficam nela. Cada instalação produz um item por vez, em fila; cada nível reduz o tempo em 10%.',
    ],
  },
  'territory.rename': {
    title: 'Renomear o país',
    body: ['Territórios consolidados podem ser renomeados. A mudança vira nota no portal de notícias.'],
  },
  'research.eras': {
    title: 'Eras de pesquisa',
    body: [
      'A pesquisa é nacional e custa dinheiro (e semicondutores a partir da Era 3). Cada Centro de pesquisa faz uma por vez: a nova vai para o Centro livre de maior nível, e os semicondutores saem do estoque dele ou, se faltar, do Centro de pesquisa que tiver mais.',
      'Para liberar uma era, conclua 60% das pesquisas da anterior.',
    ],
  },
  'territory.continent': {
    title: 'Bônus de continente',
    body: [
      'Quem tem todos os territórios de um continente recebe 10 $ por território do continente a cada turno (Ásia 350, Europa e África 230, América do Norte 180, América do Sul 160, Oceania 80) e +10% de matéria-prima e impostos nele.',
      'Ganhar e perder o domínio vira manchete.',
    ],
  },
  'research.centers': {
    title: 'Centros de pesquisa',
    body: [
      'Cada Centro de pesquisa permite 1 pesquisa simultânea (máximo 3) e reduz em 10% por nível o tempo da pesquisa feita nele.',
    ],
  },
  'logistics.modes': {
    title: 'Modos de transporte',
    body: [
      'Rodovia: 1 território/turno, 200 por rota, por territórios próprios ou aliados.',
      'Ferrovia: 2 territórios/turno, exige Terminal ferroviário na origem e no destino.',
      'Aéreo: 1 turno, qualquer distância, Aeroporto nos dois lados e Cargueiros. Marítimo: 3 zonas/turno, Porto nos dois lados e Navios mercantes (sujeitos à guerra de comboios).',
    ],
  },
  'logistics.recurring': {
    title: 'Rotas recorrentes',
    body: [
      'Repetem todo turno: uma quantidade fixa ("50 AÇO por turno") ou o excedente acima de um limite ("todo FER acima de 100").',
    ],
  },
  'logistics.sheet': {
    title: 'Estoques por território',
    body: [
      'Planilha com o estoque de cada território seu. Clique numa quantidade para enviar aquele recurso dali: escolha o modo, o destino (só os viáveis, do mais próximo ao mais distante) e a quantidade.',
    ],
  },
  'logistics.shipments': {
    title: 'Cargas em trânsito',
    body: [
      'Cargas avançam na virada. Carga em território que cair é capturada; no mar, navios inimigos e submarinos atacam comboios sem escolta.',
    ],
  },

  // ─── Inteligência ──────────────────────────────────────────────────────
  'intel.agentCount': {
    title: 'Agentes por missão',
    body: [
      'Envie de 1 a 5 agentes do mesmo território. Cada agente tenta de forma independente: basta um ter sucesso. Ex.: sabotagem (40%) com 3 agentes ≈ 78%, antes da contraespionagem.',
      'Na falha, cada agente tem 50% de chance de ser capturado. Os que escapam voltam à origem.',
    ],
  },
  'intel.agents': {
    title: 'Agentes',
    body: [
      'Produzidos na Agência de inteligência. Missões em tempo real (1 a 4 h): infiltração (tropas, instalações e estoque, 80%), interceptar ordens (50%), sabotagem (40%), fomentar revolta (50%), roubo de tecnologia (30%).',
      'Envie vários agentes na mesma missão para aumentar a chance. Missão que falha tem 50% de chance de cada agente ser capturado — e vira notícia.',
    ],
  },
  'intel.counter': {
    title: 'Contraespionagem',
    body: [
      'Reduz a chance das missões inimigas: +10% por nível de Agência no território, +5% nas vizinhas, +10% por agente estacionado, +10% com a pesquisa. Máximo 60%.',
    ],
  },
  'intel.recon': {
    title: 'Reconhecimento',
    body: [
      'Blindado de reconhecimento: território terrestre vizinho (tropas terrestres e instalações).',
      'Aviões e helicópteros: o destino exato e, nos territórios sobrevoados, a faixa de tropas. Podem ser abatidos.',
    ],
  },
  'intel.satellites': {
    title: 'Satélites',
    body: [
      'Órbita inclinada: uma faixa de latitude escolhida no lançamento (Hemisfério Norte, Equatorial ou Hemisfério Sul), com dados do turno anterior. Polar: o mundo todo, com dados do turno anterior. Estacionário: uma região (continente ou oceano) ao vivo; no painel da Agência espacial, a visão ao vivo mostra o mapa do território com as instalações.',
      'Revelam unidades e instalações, mas não ordens, estoques ou submarinos submersos. Quem tem Agência espacial vê os satélites inimigos.',
    ],
  },
  'intel.reports': {
    title: 'Relatórios',
    body: [
      'Tudo o que você descobriu, com a idade de cada dado. Informação espionada envelhece: o inimigo pode ter se movido.',
    ],
  },

  // ─── Diplomacia, notícias e placar ─────────────────────────────────────
  'diplomacy.states': {
    title: 'Estados diplomáticos',
    body: [
      'Neutro: estado inicial; não é possível atacar. Guerra: declarar é instantâneo, mas só vale no próximo turno e vira manchete.',
      'Não-agressão: permite comércio e passagem por canais; quebrar exige aviso de 2 turnos. Aliança: visão compartilhada, bases, apoio e suprimento em comum; sair leva 1 turno.',
    ],
  },
  'diplomacy.trade': {
    title: 'Comércio',
    body: [
      'Propostas de troca entre jogadores: os recursos viajam pela logística; o dinheiro é transferido na hora, com taxa de 10%.',
      'A Bolsa (aba Bolsa) é a válvula de escape: lotes fixos por turno, com preço que varia com a procura e a escassez; vender é livre.',
    ],
  },
  'diplomacy.chat': {
    title: 'Mensagens',
    body: ['Canal global, canal da aliança e mensagens diretas. Bots não usam o chat.'],
  },
  'news.levels': {
    title: 'O portal de notícias',
    body: [
      'Cada turno gera uma edição. Manchetes: ataque nuclear, queda de capital, eliminação, guerra, traição. Notícias: conquistas, grandes batalhas, bombardeios, revoltas. Notas: tratados, espiões capturados, satélites.',
      'As notícias só revelam o que seria público: um bombardeio não detectado aparece como "origem desconhecida".',
    ],
  },
  'news.communique': {
    title: 'Comunicados oficiais',
    body: [
      'Com a Imprensa estatal você publica comunicados no portal (1 por turno; 2 a partir do nível 3). Podem ser propaganda — ou mentira.',
    ],
  },
  'score.metrics': {
    title: 'Pontuação de Poder',
    body: [
      'Território consolidado 10 (capital inicial 30), ocupado 4, zona marítima controlada 2, 1 ponto a cada 10 $ de PIB, 1 a cada 100 $ de poder militar e 3 por pesquisa.',
      'PIB = valor de tudo o que foi produzido no turno. Poder militar = custo de todas as unidades vivas.',
      'Continentes: o governo que domina um continente inteiro recebe o bônus de continente.',
    ],
  },

  // ─── Governo, simulador, admin, conta ──────────────────────────────────
  'government.editor': {
    title: 'Seu governo',
    body: ['Nome, tipo (cosmético, aparece nas notícias), cores e bandeira. Você pode mudar quando quiser.'],
  },
  'simulator.combat': {
    title: 'Simulador de combate',
    body: [
      'Monte dois lados e veja forças, vencedor e perdas usando as mesmas regras do jogo, sem afetar a partida. Bom para planejar ataques.',
    ],
  },
  'admin.actions': {
    title: 'Ações administrativas',
    body: [
      'Forçar virada enfileira a resolução do turno agora. Rollback restaura o snapshot do turno N.',
      '"→ Bot" passa um governo a um bot. Os alertas mostram contas que compartilham IP ou navegador numa partida (só alerta, sem banimento).',
    ],
  },
  'account.alerts': {
    title: 'Avisos',
    body: [
      'Depois de cada virada, o resumo do turno mostra os ataques que você sofreu, o resultado dos seus ataques e as notícias sobre o seu governo — sem precisar abrir o jornal ou o log.',
      'Durante o turno, avisos no canto da tela informam contato radar, espiões capturados, declarações de guerra e novas propostas. Clique num aviso para abrir a seção.',
    ],
  },
  'account.tutorial': {
    title: 'Tutorial',
    body: [
      'Os tutoriais aparecem na primeira vez em cada tela. Você pode revê-los a qualquer momento pelo botão "?" no cabeçalho da partida.',
    ],
  },
} satisfies Record<string, HelpTopic>;

export type HelpId = keyof typeof HELP;
